import { Component, inject, Input, TemplateRef } from '@angular/core';
import { ExportColumnDef, getDataMeasures } from '../data-tree-table/data-tree-table';
import { DataResponse, DataService, ExportFilter, FilterExpression } from '../../../services/data.service';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { fromPairs, isFunction, isUndefined, last, omitBy, zip } from 'lodash-es';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { CardSortHeadersComponent, SortHeader } from './card-sort-headers/card-sort-headers.component';
import { Sort } from '../../components/table/table/table.component';
import { PartialPath, Path, shrub, sortByMeasure } from '../../../services/data-tree';
import { DataErrorMessage, DataErrorMessageEnum } from '@cumlaude/service-contract';
import { CardListConfig } from './card-list-config';
import { Attributes } from '../base-dashboard/base-dashboard-config';
import { DashboardContext } from '../base-dashboard/dashboard-context';
import { BaseDashboardComponent } from '../base-dashboard/base-dashboard.component';
import { DashboardVariant } from '../../../services/weergave-opties';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ExportOptions } from '../../../services/export.service';
import { TableExport } from '../../../services/exportable';
import { isErrorContext, isHappyContext } from '../../../services/error-util';

export function getGroupKeys<G>(groupNames: string[], path: PartialPath<unknown, unknown>): G {
	const groupLevels = path.slice(1, groupNames.length + 1);
	return <G>fromPairs(
		zip(
			groupNames,
			groupLevels.map((lvl) => lvl.k)
		)
	);
}

export interface ExportCardListColumnDef<
	I extends Attributes,
	G,
	A extends Attributes,
	C extends CardListConfig<I, G, A>,
> extends ExportColumnDef<A> {
	getValue(path: Path<A, number[]>, context: DashboardContext<I, A, C>): any;
}

@Component({
	selector: 'app-card-list',
	templateUrl: './card-list.component.html',
	styleUrls: ['./card-list.component.scss'],
	imports: [CardSortHeadersComponent, NgTemplateOutlet, AsyncPipe],
})
export class CardListComponent<I extends Attributes, G, A extends Attributes, C extends CardListConfig<I, G, A>> extends BaseDashboardComponent<
	I,
	A,
	C
> {
	protected readonly dataService = inject(DataService);

	@Input()
	sortHeaders: SortHeader[] = [];

	@Input()
	sortMeasure?: (row: Path<A, unknown>) => number;

	@Input()
	template!: TemplateRef<any>;

	/**
	 * Hiermee kan de DashboardVariant "vertraagd" naar de template doorgegeven worden, dwz pas als het viewmodel geüpdate is.
	 */
	@Input()
	variant?: DashboardVariant;

	@Input()
	elementPageBreak: boolean = false;

	private readonly cardClick$ = new Subject<Path<A, number[]>>();

	vm$ = new ReplaySubject<{ context?: DashboardContext<I, A, CardListConfig<I, G, A>>; error?: DataErrorMessage; variant?: DashboardVariant }>(1);

	public ErrorEnum: typeof DataErrorMessageEnum = DataErrorMessageEnum;

	constructor() {
		super();

		this.subscriptions.push(
			this.dashboardContext$
				.pipe(
					filter((value) => isHappyContext(value)),
					switchMap((context) => this.cardClick$.pipe(map((row) => this.config.createSelection(context, row))))
				)
				.subscribe((selection) => this.urlService.navigateToSelection(selection)),
			this.dashboardContext$
				.pipe(
					switchMap((context) => {
						if (isErrorContext(context)) {
							return of({
								error: context,
							});
						}

						if (!isUndefined(this.sortMeasure)) {
							if (!isUndefined(context.dataRoot)) sortByMeasure(context.dataRoot, this.sortMeasure, 'desc');

							return of({
								context,
								variant: this.variant,
							});
						}

						if (this.sortHeaders.length == 0) {
							return of({
								context,
								variant: this.variant,
							});
						}

						return this.qp.observe('sortOrder', this.getDefaultSortOrder()).pipe(
							map((sortState) => {
								if (!isUndefined(context.dataRoot)) this.sortData(sortState, context);

								return {
									context,
									variant: this.variant,
								};
							})
						);
					})
				)
				.subscribe(this.vm$)
		);
	}

	protected override createDashboardContext(resp: DataResponse<number[]>, f: FilterExpression): DashboardContext<I, A, C> {
		const context = super.createDashboardContext(resp, f);
		if (!context.dataRoot) return context;
		const dataRoot = shrub(context.dataRoot);
		return { ...context, dataRoot };
	}

	private getDefaultSortOrder(): Sort {
		if (this.sortHeaders.length == 0) return { active: '', direction: '' };

		const defaultSortHeader = this.sortHeaders[0];
		return {
			active: defaultSortHeader.sortTarget.join('.'),
			direction: defaultSortHeader.defaultDirection,
		};
	}

	private sortData(sortState: Sort, context: DashboardContext<I, A, CardListConfig<I, G, A>>) {
		const elements = [...context.groupNames, ...context.measureNames];
		const sortIndex = elements.indexOf(sortState.active) + 1;

		sortByMeasure(
			context.dataRoot!,
			(path: Path<A, number[]>) => {
				const firstRow = last(path)!.r[0];
				const keys = [...firstRow.map((value) => value.k), ...(<number[]>last(firstRow)!.v)];
				return keys[sortIndex];
			},
			<'asc' | 'desc'>sortState.direction
		);
	}

	onCardClick(row: Path<A, number[]>) {
		if (!this.config.clickable) return;
		this.cardClick$.next(row);
	}

	getGroupKeys(row: Path<A, number[]>, context: DashboardContext<I, A, CardListConfig<I, G, A>>): G {
		return getGroupKeys(context.groupNames, row);
	}

	getMeasures(row: Path<A, number[]>, context: DashboardContext<I, A, CardListConfig<I, G, A>>): Partial<Attributes> {
		return getDataMeasures(context.measureNames, <number[]>last(row)!.v);
	}

	exportAsTable(exportOptions: ExportOptions, title: string, filters: ExportFilter[]): Observable<Blob> {
		return this.vm$.pipe(
			take(1),
			map(({ context }) => this.getExportTable(context!, exportOptions, title, filters)),
			switchMap((exportTable) => this.dataService.getTableExport(exportTable))
		);
	}

	protected getExportTable(
		context: DashboardContext<I, A, CardListConfig<I, G, A>>,
		exportOptions: ExportOptions,
		title: string,
		filters: ExportFilter[]
	): TableExport<A> {
		const exportTable = context.config.getExportTable(context, exportOptions, title, filters);
		if (exportTable) return exportTable;

		const columns = context.config.getExportColumnDefs(context);
		const rows =
			context?.dataRoot?.r.map((path) => {
				return columns.map((col, ix) => {
					const format = isFunction(col.format) ? col.format({ _id: ix.toString(), _path: path }) : undefined;
					const value = col.getValue(path, context);
					const type = isFunction(col.type) ? col.type({ _id: ix.toString(), _path: path }) : undefined;
					if (format || type) {
						return omitBy({ value, format, type }, isUndefined);
					}
					return value;
				});
			}) ?? [];
		return this.config.buildSinglePageExportTable(exportOptions, title, filters, [{ columns, rows }]);
	}
}
